import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { SuccessContext } from "@app/components/layout/success-snackbar"
import { MyUserUpdateDto } from "@app/core/dto/user/my-user-update-dto"
import { User } from "@app/core/dto/user/user"
import { getErrorCode, getErrorMessage } from "@app/core/error/error-handler"
import { useUser } from "@app/core/hooks/use-user"
import { getUserFromStorage } from "@app/core/services/authentication-service"
import { Children } from "@app/core/type/Children"
import { TokenContext } from "./TokenContext"

export const UserContext = React.createContext<UserStore>({} as UserStore)

export function UserContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const initialUser: User | undefined = useMemo(() => getUserFromStorage(), [])

  const { getCurrentUser, updateMyUser, sendUserConnexionInformation } = useUser()
  const openSuccessSnackbar: (message: string) => void = React.useContext(SuccessContext)
  const { cognitoUserIdRef, unsetCookieAndUserId, isAuthenticated } = React.useContext(TokenContext)

  // Whether this load of the application found a Cognito session still alive: nobody submitted the sign-in form,
  // which is the only other place an opening is recorded. Captured on the first render, before any token refresh.
  const openedWithLiveSession = useRef<boolean>(!!cognitoUserIdRef.current)
  const openingRecorded = useRef<boolean>(false)

  const [user, setUser] = useState<User | undefined>(initialUser)
  const [userLoading, setUserLoading] = useState<boolean>(false)
  const [isFromLogoutButton, setIsFromLogoutButton] = useState<boolean>(false)
  const [loginError, setLoginError] = useState<string>()

  useEffect(() => {
    // Clean user context when 'unsetCookieAndUserId' is called, when the cookies and tokens are cleaned
    if (!isAuthenticated) {
      setUser(undefined)
    }
  }, [isAuthenticated])

  const logout = useCallback(async () => {
    await unsetCookieAndUserId()
  }, [unsetCookieAndUserId])

  /**
   * Records the opening of the application for a user who came back with a session still alive. The sign-in page
   * records the openings that go through the form, and keeps that call because it also detects the header the proxy
   * refuses; this one covers the returns, which used to leave no trace at all — neither `User.lastConnection` nor a
   * LOGIN event in the activity journal.
   *
   * <p>Once per mount of this provider, which is once per load of the front: it sits above the router. The phone
   * guard above it does swap the whole tree when the screen crosses its breakpoint, and that remount records a
   * second opening — the application re-initialises entirely there, so counting it again is no lie.</p>
   */
  const recordApplicationOpening: () => void = useCallback(() => {
    if (!openedWithLiveSession.current || openingRecorded.current) {
      return
    }
    openingRecorded.current = true
    // Telemetry only: a failure here must never keep the user out of the application.
    sendUserConnexionInformation().catch((reason) => console.error("opening not recorded: ", reason))
  }, [sendUserConnexionInformation])

  const getCurrentUserConnexion: () => void = useCallback(
    () => {
      if (cognitoUserIdRef.current && !userLoading) {
        setUserLoading(true)
        getCurrentUser()
          .then((newUser) => {
            setUser(newUser)
            recordApplicationOpening()
          })
          .catch((reason) => {
            console.error("reason: ", reason)
            // This provider sits above the error snackbar, so the sign-in page shows the refusal itself.
            // US 2554 — a collaborator blocked by the administration is told so the same way
            if (["ORGANIZATION_IS_BLOCKED", "USER_IS_BLOCKED"].includes(getErrorCode(reason) ?? "")) {
              setLoginError(getErrorMessage(reason))
            }
            return logout()
          })
          .finally(() => setUserLoading(false))
      } else if (!userLoading) {
        setUser(undefined)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- userLoading stays out: a new callback each time a load ends would make the effect below load the user again
    [logout, getCurrentUser, unsetCookieAndUserId, cognitoUserIdRef, recordApplicationOpening]
  )

  useEffect(() => {
    getCurrentUserConnexion()
  }, [getCurrentUserConnexion])

  useEffect(() => {
    if (user === undefined) {
      localStorage.removeItem("user")
    } else {
      localStorage.setItem("user", JSON.stringify(user))
    }
  }, [user])

  const updateUserContext: (userUpdateDto: MyUserUpdateDto) => Promise<void> = useCallback(
    async (userUpdateDto) => {
      console.info("update user")
      try {
        setUserLoading(true)
        const updatedUser = await updateMyUser(userUpdateDto)
        setUser((prevUser) => {
          if (prevUser) {
            return {
              ...prevUser,
              givenName: updatedUser.givenName,
              email: updatedUser.email,
              familyName: updatedUser.familyName,
              phone: updatedUser.phone,
            }
          } else {
            logout()
            return undefined
          }
        })
        openSuccessSnackbar("Modification réussie !")
      } finally {
        setUserLoading(false)
      }
    },
    [logout, openSuccessSnackbar, updateMyUser]
  )

  const userStore: UserStore = useMemo(
    () => ({
      user,
      userLoading,
      updateUserContext,
      logout,
      isFromLogoutButton,
      setIsFromLogoutButton,
      getCurrentUserConnexion,
      loginError,
      clearLoginError: () => setLoginError(undefined),
    }),
    [user, userLoading, updateUserContext, logout, isFromLogoutButton, getCurrentUserConnexion, loginError]
  )
  return <UserContext.Provider value={userStore}>{children}</UserContext.Provider>
}

export interface UserStore {
  user: User | undefined
  userLoading: boolean
  isFromLogoutButton: boolean

  setIsFromLogoutButton: Dispatch<SetStateAction<boolean>>
  updateUserContext(userUpdateDto: MyUserUpdateDto): void
  logout(): Promise<void>
  getCurrentUserConnexion(): void
  /** Why the last sign-in was refused after Cognito accepted it (blocked organization). */
  loginError: string | undefined
  clearLoginError(): void
}
